// @ts-nocheck
/*
 * Port of views/dashboard/api-metrics/list.js (802 lines). BYTE-IDENTICAL.
 *
 * SUPERADMIN-only API health dashboard reading from the same
 * `${API_BACKEND_LOGS}` endpoint as Backend Logs but with `type=api_metrics`
 * and structured `parseLogLine` extraction. Renders KPI tiles (Total,
 * Avg/P95/Max response, Error Rate), 5 ApexCharts (requests over time,
 * avg response over time, status-code donut, method bar, Top 10 slowest
 * endpoints horizontal bar) and a large paginated table.
 *
 * Also embeds an inline API Editor: pick method/path, send raw headers +
 * JSON body, view response status/timing/body â€” used for quick probes
 * against BACKEND_API_BASE_URL directly.
 *
 * PrimeReact DataTable â†’ obd3-gui DataTable; PrimeReact InputText â†’
 * react-bootstrap Form.Control; PrimeReact Button â†’ react-bootstrap Button.
 *
 * V1 QUIRKS preserved verbatim:
 *  - Time-bucket key is IST (`Asia/Kolkata`, `HH:mm`, 24-hour) â€” not user
 *    locale.
 *  - `statusColor` treats >=400 and >=500 with the same colour (#ef4444);
 *    only the >=300 (blue) and default (green) buckets differ.
 *  - The apex-chart tooltips are dark-themed regardless of dashboard
 *    theme.
 *  - The "Top 10 slowest endpoints" bar sits in `gridColumn: "1 / -1"`
 *    spanning both grid columns.
 *  - `fmtTime` uses IST with `hour12: true` and a comma-less short date.
 *  - Reset re-applies default filters (both filters and appliedFilters).
 *  - `filters.timeRange` reset to '15' (15 min) on Reset â€” not '5'.
 *  - Non-superadmin viewers see a plain "Access Denied" div (no ACL panel).
 */
import React, { useEffect, useMemo, useRef, useState } from 'react';
import { usePersistedState } from '@/lib/filters/persisted-state';
import type { ColumnDef } from '@tanstack/react-table';
import { DataTable } from '@/components/data-table/DataTable';
import Loader from '@/components/partials/components/Loader';
import { getApiCall } from '@/service/httpCall';
import { toast } from 'sonner';
import { friendlyApiError } from '@/utilities/apiErrorMessage';
import { USER_ROLES } from '@/utilities/constant';
import { Button as _ShadButton } from '@/components/ui/button';

const _cx = (...args: any[]) => args.filter(Boolean).join(' ');
const _btnVariantMap: Record<string, any> = {
  primary: 'default',
  secondary: 'outline',
  danger: 'destructive',
  light: 'outline',
  link: 'link',
};
const Button: any = ({ variant, size, className, children, ...props }: any) => {
  let mapped = variant;
  if (typeof variant === 'string') {
    if (variant.startsWith('outline-')) mapped = 'outline';
    else if (_btnVariantMap[variant]) mapped = _btnVariantMap[variant];
    else if (variant.startsWith('btn ')) mapped = 'default';
  }
  return (
    <_ShadButton variant={mapped} size={size} className={className} {...props}>
      {children}
    </_ShadButton>
  );
};
const Form: any = ({ children, onSubmit, className, ...props }: any) => (
  <form
    className={className}
    onSubmit={onSubmit || ((e: any) => e.preventDefault())}
    {...props}
  >
    {children}
  </form>
);
Form.Group = ({ className, controlId: _c, children, ...props }: any) => (
  <div className={_cx('mb-3', className)} {...props}>
    {children}
  </div>
);
Form.Label = ({ className, children, htmlFor, ...props }: any) => (
  <label className={_cx('form-label', className)} htmlFor={htmlFor} {...props}>
    {children}
  </label>
);
Form.Control = ({ as, className, children, isInvalid, ...props }: any) => {
  const cls = _cx('form-control', isInvalid && 'is-invalid', className);
  if (as === 'select')
    return (
      <select className={cls} {...props}>
        {children}
      </select>
    );
  if (as === 'textarea') return <textarea className={cls} {...props} />;
  return <input className={cls} {...props} />;
};
Form.Select = ({ className, children, isInvalid, ...props }: any) => (
  <select
    className={_cx('form-control form-select', isInvalid && 'is-invalid', className)}
    {...props}
  >
    {children}
  </select>
);
Form.Check = ({
  type = 'checkbox',
  label,
  id,
  className,
  inline,
  children,
  ...props
}: any) => (
  <div className={_cx('form-check', inline && 'form-check-inline', className)}>
    <input type={type} id={id} className="form-check-input" {...props} />
    {label !== undefined && (
      <label htmlFor={id} className="form-check-label">
        {label}
      </label>
    )}
    {children}
  </div>
);
import Chart from 'react-apexcharts';
import { API_BACKEND_LOGS } from '@/service/url';
import { Storage, getUserAccessToken } from '@/utilities/setting';
import { BACKEND_API_BASE_URL, CLIENT_SOURCE } from '@/utilities/constant';
import { FillPage } from '@/components/layout/PageContainer';
import { PageHeader } from '@/components/feedback/PageHeader';
import { Button as ShadPageButton } from '@/components/ui/button';
import { Link2, TerminalSquare, ArrowUpRight, ArrowDownLeft, Copy } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from '@/components/ui/dialog';

const isEmpty = (v: any) =>
  v == null || (typeof v === 'object' && Object.keys(v).length === 0);

const JsonBlock = ({ label, value }: any) => {
  const text = isEmpty(value) ? '' : typeof value === 'string' ? value : JSON.stringify(value, null, 2);
  return (
    <div className="space-y-1">
      <div className="flex items-center justify-between">
        <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
          {label}
        </span>
        {text && (
          <button
            type="button"
            className="inline-flex items-center gap-1 text-[11px] text-muted-foreground hover:text-foreground"
            onClick={() => {
              navigator.clipboard?.writeText(text);
              toast.success(`${label} copied`);
            }}
          >
            <Copy className="h-3 w-3" /> Copy
          </button>
        )}
      </div>
      {text ? (
        <pre className="max-h-[320px] overflow-auto whitespace-pre-wrap break-words rounded-md border border-border/60 bg-muted/40 p-2.5 font-mono text-xs text-foreground">
          {text}
        </pre>
      ) : (
        <div className="rounded-md border border-dashed border-border/60 px-2.5 py-2 text-xs text-muted-foreground">
          Empty
        </div>
      )}
    </div>
  );
};
import { CalendarTimeInput } from '@/components/ui/calendar-inputs';

const timeOptions = [
  { label: 'Last 5 mins', value: '5' },
  { label: 'Last 15 mins', value: '15' },
  { label: 'Last 30 mins', value: '30' },
  { label: 'Last 45 mins', value: '45' },
  { label: 'Whole Day', value: 'day' },
  { label: 'Custom', value: 'custom' },
];

const methodOptions = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'];

function respTimeColor(ms: any) {
  if (ms == null) return '#6b7280';
  if (ms < 300) return '#22c55e';
  if (ms < 1000) return '#f59e0b';
  return '#ef4444';
}

function statusColor(code: any) {
  if (code >= 500) return '#ef4444';
  if (code >= 400) return '#ef4444';
  if (code >= 300) return '#3b82f6';
  return '#22c55e';
}

function getTodayDate() {
  return new Date().toISOString().split('T')[0];
}

function getYesterdayDate() {
  const d = new Date();
  d.setDate(d.getDate() - 1);
  return d.toISOString().split('T')[0];
}

function parseLogLine(line: string) {
  const braceIdx = line.indexOf('{');
  if (braceIdx === -1) return null;
  try {
    return JSON.parse(line.slice(braceIdx));
  } catch (e) {
    return null;
  }
}

const EMPTY_STATS = {
  total: 0,
  avgMs: 0,
  p95Ms: 0,
  maxMs: 0,
  errorRate: 0,
  timeBuckets: [],
  countSeries: [],
  avgSeries: [],
  statusDist: { '2xx': 0, '3xx': 0, '4xx': 0, '5xx': 0 },
  methodDist: {},
  slowest: [],
};

const ApiMetricsPage = () => {
  // `metrics` is the current page only; KPIs / charts come from the server
  // aggregate over every matching row (`stats`).
  const [metrics, setMetrics] = useState<any[]>([]);
  const [stats, setStats] = useState<any>(EMPTY_STATS);
  const [total, setTotal] = useState(0);
  const [paging, setPaging] = usePersistedState('api-metrics/ApiMetrics:paging', { pageIndex: 0, pageSize: 100 }, {
    // A new day means new rows: start from the first page.
    onRestore: (saved) => ({ ...saved, pageIndex: 0 }),
  });
  const [loading, setLoading] = useState(false);
  const [pageLoading, setPageLoading] = useState(false);
  const reqSeq = useRef(0);
  const statsFor = useRef<any>(null);
  const [globalFilter, setGlobalFilter] = usePersistedState('api-metrics/ApiMetrics:globalFilter', '');
  const [loggedInUserRole, setLoggedInUserRole] = useState<any>(null);

  // The date always opens on today; the other filters are kept.
  const [filters, setFilters] = usePersistedState<any>('api-metrics/ApiMetrics:filters', {
    date: getTodayDate(),
    timeRange: '15',
    fromTime: '',
    toTime: '',
    method: '',
    minMs: '',
    search: '',
  }, {
    onRestore: (saved) => ({ ...saved, date: getTodayDate() }),
  });
  const [appliedFilters, setAppliedFilters] = usePersistedState<any>('api-metrics/ApiMetrics:appliedFilters', filters, {
    onRestore: (saved) => ({ ...saved, date: getTodayDate() }),
  });

  const [showEditor, setShowEditor] = useState(false);
  const [reqMethod, setReqMethod] = useState('GET');
  const [reqPath, setReqPath] = useState('/v2/');
  const [reqHeaders, setReqHeaders] = useState(
    JSON.stringify(
      {
        'Content-Type': 'application/json',
        Authorization: getUserAccessToken() || '',
      },
      null,
      2,
    ),
  );
  const [reqBody, setReqBody] = useState('{}');
  const [sending, setSending] = useState(false);
  const [response, setResponse] = useState<any>(null);
  const [detail, setDetail] = useState<{ kind: 'request' | 'response'; row: any } | null>(null);

  const buildFullUrl = () => {
    const base = (BACKEND_API_BASE_URL || '').replace(/\/+$/, '');
    const p = reqPath.startsWith('/') ? reqPath : `/${reqPath}`;
    return `${base}${p}`;
  };

  const sendRequest = async () => {
    setSending(true);
    setResponse(null);

    let headers: any = {};
    try {
      headers = reqHeaders.trim() ? JSON.parse(reqHeaders) : {};
    } catch (e) {
      toast.error('Headers must be valid JSON');
      setSending(false);
      return;
    }

    headers['x-client-source'] = CLIENT_SOURCE;

    const opts: any = { method: reqMethod, headers };
    if (!['GET', 'HEAD'].includes(reqMethod) && reqBody.trim()) {
      opts.body = reqBody;
    }

    const start = performance.now();
    try {
      const res = await fetch(buildFullUrl(), opts);
      const timeMs = Math.round((performance.now() - start) * 100) / 100;
      const text = await res.text();
      let parsed: any = text;
      try {
        parsed = JSON.stringify(JSON.parse(text), null, 2);
      } catch (e) {
        /* leave as raw text */
      }
      const respHeaders: any = {};
      res.headers.forEach((v: any, k: any) => {
        respHeaders[k] = v;
      });
      setResponse({
        status: res.status,
        ok: res.ok,
        timeMs,
        headers: respHeaders,
        body: parsed,
      });
    } catch (err: any) {
      setResponse({
        status: 0,
        ok: false,
        timeMs: Math.round((performance.now() - start) * 100) / 100,
        headers: {},
        body: `Request failed: ${err.message}`,
      });
    } finally {
      setSending(false);
    }
  };

  useEffect(() => {
    const rawUser = Storage.getItem('user');
    if (!rawUser) return;
    try {
      const user = JSON.parse(rawUser);
      setLoggedInUserRole(user.role);
    } catch (e) {
      console.error('Invalid user JSON', e);
    }
  }, []);

  useEffect(() => {
    if (!loggedInUserRole) return;
    if (loggedInUserRole !== USER_ROLES.SUPERADMIN) return;
    fetchMetrics();
  }, [loggedInUserRole, appliedFilters, paging]);

  const fetchMetrics = () => {
    const seq = ++reqSeq.current;
    const withStats = statsFor.current !== appliedFilters;
    (withStats ? setLoading : setPageLoading)(true);
    const offset = paging.pageIndex * paging.pageSize;

    const query =
      `?type=api_metrics` +
      `&date=${appliedFilters.date}` +
      `&level=` +
      `&search=${encodeURIComponent(appliedFilters.search || '')}` +
      `&timeRange=${appliedFilters.timeRange}` +
      `&fromTime=${appliedFilters.fromTime || ''}` +
      `&toTime=${appliedFilters.toTime || ''}` +
      `&method=${appliedFilters.method || ''}` +
      `&minMs=${appliedFilters.minMs || ''}` +
      `&page=${paging.pageIndex + 1}` +
      `&limit=${paging.pageSize}` +
      `&stats=${withStats ? 1 : 0}`;

    getApiCall(`${API_BACKEND_LOGS}${query}`)
      .then((res: any) => {
        if (seq !== reqSeq.current) return;
        setMetrics(
          (res?.rows || [])
            .map((line: any, index: number) => {
              const parsed = parseLogLine(line);
              return parsed ? { id: offset + index + 1, ...parsed } : null;
            })
            .filter(Boolean),
        );
        setTotal(Number(res?.total) || 0);
        if (withStats) {
          setStats(res?.stats || EMPTY_STATS);
          statsFor.current = appliedFilters;
        }
      })
      .catch((err: any) => {
        if (seq !== reqSeq.current) return;
        toast.error(friendlyApiError(err) || 'Failed to load metrics');
      })
      .finally(() => {
        if (seq !== reqSeq.current) return;
        setLoading(false);
        setPageLoading(false);
      });
  };

  const applyFilters = (next: any) => {
    setAppliedFilters(next);
    setPaging((p) => ({ ...p, pageIndex: 0 }));
  };

  const resetFilters = () => {
    const def = {
      date: getTodayDate(),
      timeRange: '15',
      fromTime: '',
      toTime: '',
      method: '',
      minMs: '',
      search: '',
    };
    setFilters(def);
    applyFilters(def);
  };

  // Client-side globalFilter: search across method/path/url/host/requestId.
  // MUST be declared before any conditional early return below â€” the previous
  // ordering (early returns for Access Denied / loading, then this useMemo)
  // violated the Rules of Hooks and threw "Rendered fewer hooks than expected"
  // on the second render when loggedInUserRole loaded as non-SUPERADMIN.
  const displayRows = useMemo(() => {
    if (!globalFilter.trim()) return metrics;
    const q = globalFilter.trim().toLowerCase();
    return metrics.filter((r: any) =>
      [r.method, r.path, r.url, r.host, r.origin, r.requestId, r.ip]
        .filter(Boolean)
        .some((f: any) => String(f).toLowerCase().includes(q)),
    );
  }, [metrics, globalFilter]);

  if (loggedInUserRole && loggedInUserRole !== USER_ROLES.SUPERADMIN) {
    return (
      <FillPage>
        <PageHeader title="API Response Time Metrics" />
        <div className="rounded-md border border-border bg-muted/30 p-8 text-center text-sm text-muted-foreground">
          Access denied. API metrics are visible to super-admins only.
        </div>
      </FillPage>
    );
  }

  if (loading) return <Loader />;

  const fmtTime = (iso: any) => {
    if (!iso) return '-';
    const d = new Date(iso);
    return d.toLocaleString('en-IN', {
      timeZone: 'Asia/Kolkata',
      day: '2-digit',
      month: 'short',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      hour12: true,
    });
  };

  const columns: ColumnDef<any>[] = [
    {
      id: 'index',
      header: '#',
      size: 60,
      enableSorting: false,
      cell: ({ row }: any) => (
        <span className="text-xs tabular-nums text-muted-foreground">
          {row.original.id}
        </span>
      ),
    },
    {
      accessorKey: 'timestamp',
      header: 'Time',
      cell: ({ row }: any) => (
        <span className="font-mono text-[11px] text-muted-foreground">
          {fmtTime(row.original.timestamp)}
        </span>
      ),
    },
    {
      accessorKey: 'method',
      header: 'Method',
      size: 90,
      cell: ({ row }: any) => {
        const m = String(row.original.method || '').toUpperCase();
        const tone =
          m === 'GET'
            ? 'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300'
            : m === 'POST'
              ? 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300'
              : m === 'PUT' || m === 'PATCH'
                ? 'bg-amber-100 text-amber-800 ring-amber-200 dark:bg-amber-950/40 dark:text-amber-300'
                : m === 'DELETE'
                  ? 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
                  : 'bg-muted/60 text-muted-foreground ring-border/60';
        return (
          <span
            className={
              'inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[10px] font-bold ring-1 ring-inset ' +
              tone
            }
          >
            {m || '—'}
          </span>
        );
      },
    },
    {
      accessorKey: 'path',
      header: 'Path',
      cell: ({ row }: any) => (
        <span className="font-mono text-xs text-foreground">
          {row.original.path || '—'}
        </span>
      ),
    },
    {
      id: 'url',
      header: 'URL',
      size: 80,
      enableSorting: false,
      cell: ({ row }: any) => {
        const url = row.original.url;
        if (!url) return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <span
            className="group inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/15 cursor-help"
            title={url}
          >
            <Link2 className="h-3.5 w-3.5" />
          </span>
        );
      },
    },
    {
      accessorKey: 'host',
      header: 'Host',
      cell: ({ row }: any) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.original.host || row.original.origin || '—'}
        </span>
      ),
    },
    {
      accessorKey: 'statusCode',
      header: 'Status',
      size: 90,
      cell: ({ row }: any) => {
        const c = Number(row.original.statusCode);
        if (!c) return <span className="text-xs text-muted-foreground">—</span>;
        const tone =
          c >= 500
            ? 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
            : c >= 400
              ? 'bg-rose-100 text-rose-700 ring-rose-200 dark:bg-rose-950/40 dark:text-rose-300'
              : c >= 300
                ? 'bg-sky-100 text-sky-700 ring-sky-200 dark:bg-sky-950/40 dark:text-sky-300'
                : 'bg-emerald-100 text-emerald-700 ring-emerald-200 dark:bg-emerald-950/40 dark:text-emerald-300';
        return (
          <span
            className={
              'inline-flex items-center rounded-full px-2 py-0.5 font-mono text-[11px] font-bold tabular-nums ring-1 ring-inset ' +
              tone
            }
          >
            {c}
          </span>
        );
      },
    },
    {
      accessorKey: 'responseTimeMs',
      header: 'Response (ms)',
      size: 120,
      cell: ({ row }: any) => {
        const ms = row.original.responseTimeMs;
        if (ms == null) return <span className="text-xs text-muted-foreground">—</span>;
        return (
          <span
            className="font-mono text-xs font-bold tabular-nums"
            style={{ color: respTimeColor(ms) }}
          >
            {ms} ms
          </span>
        );
      },
    },
    {
      id: 'ip',
      header: 'IP',
      cell: ({ row }: any) => (
        <span className="font-mono text-xs text-muted-foreground">
          {row.original.ip || '—'}
        </span>
      ),
    },
    {
      id: 'requestId',
      header: 'Request ID',
      cell: ({ row }: any) => (
        <span className="font-mono text-[10px] text-muted-foreground">
          {row.original.requestId || '—'}
        </span>
      ),
    },
    ...(['request', 'response'] as const).map((kind) => ({
      id: kind,
      header: kind === 'request' ? 'Request' : 'Response',
      size: 90,
      enableSorting: false,
      cell: ({ row }: any) => {
        const Icon = kind === 'request' ? ArrowUpRight : ArrowDownLeft;
        return (
          <button
            type="button"
            onClick={() => setDetail({ kind, row: row.original })}
            title={`View ${kind}`}
            className="inline-flex h-8 w-8 items-center justify-center rounded-md bg-primary/10 text-primary ring-1 ring-inset ring-primary/20 transition-colors hover:bg-primary/20"
          >
            <Icon className="h-3.5 w-3.5" />
          </button>
        );
      },
    })),
  ];

  return (
    <FillPage>
      <PageHeader
        title="API Response Time Metrics"
        description="Backend API latency, error rate, and slowest-endpoint dashboard."
        actions={
          <ShadPageButton
            variant={showEditor ? 'outline' : 'default'}
            onClick={() => setShowEditor((s) => !s)}
          >
            <TerminalSquare className="h-4 w-4" />
            {showEditor ? 'Hide API Editor' : 'API Editor'}
          </ShadPageButton>
        }
      />
      <div className="dash-section-card">

      {showEditor && (
        <div
          className="mb-3 flex flex-col gap-3 rounded-xl border border-border/60 bg-card p-4"
          style={{
            boxShadow:
              '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
          }}
        >
          <div className="flex flex-wrap items-stretch gap-2">
            <select
              value={reqMethod}
              onChange={(e) => setReqMethod(e.target.value)}
              className="input-embossed h-10 w-[110px] rounded-md px-3 text-sm"
            >
              {['GET', 'POST', 'PUT', 'PATCH', 'DELETE'].map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>

            <span
              className="inline-flex items-center whitespace-nowrap rounded-md border border-border/60 bg-muted/40 px-2.5 font-mono text-xs text-muted-foreground"
              title={BACKEND_API_BASE_URL}
            >
              {BACKEND_API_BASE_URL}
            </span>

            <input
              value={reqPath}
              onChange={(e) => setReqPath(e.target.value)}
              placeholder="/v2/..."
              className="input-embossed h-10 min-w-[200px] flex-1 rounded-md px-3 font-mono text-sm"
            />

            <Button
              onClick={sendRequest}
              disabled={sending}
              className="h-10 gap-1.5 bg-emerald-600 text-white hover:bg-emerald-700"
            >
              {sending ? 'Sending…' : 'Send'}
            </Button>
          </div>

          <div className="break-all font-mono text-[11px] text-muted-foreground">
            {reqMethod} {buildFullUrl()}
          </div>

          <div className="flex flex-wrap gap-3">
            <div className="min-w-[260px] flex-1 space-y-1">
              <label className="text-xs font-semibold text-muted-foreground">
                Headers (JSON)
              </label>
              <textarea
                value={reqHeaders}
                onChange={(e) => setReqHeaders(e.target.value)}
                rows={6}
                className="w-full rounded-md border border-border/60 bg-card p-2 font-mono text-xs text-foreground shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)] outline-none focus:border-ring"
              />
            </div>
            <div className="min-w-[260px] flex-1 space-y-1">
              <label className="text-xs font-semibold text-muted-foreground">
                Body{' '}
                {['GET', 'HEAD'].includes(reqMethod)
                  ? '(ignored for GET)'
                  : '(JSON / raw)'}
              </label>
              <textarea
                value={reqBody}
                onChange={(e) => setReqBody(e.target.value)}
                rows={6}
                disabled={['GET', 'HEAD'].includes(reqMethod)}
                className="w-full rounded-md border border-border/60 bg-card p-2 font-mono text-xs text-foreground shadow-[inset_2px_2px_5px_rgba(163,177,198,0.35),inset_-2px_-2px_5px_rgba(255,255,255,0.9)] outline-none focus:border-ring disabled:cursor-not-allowed disabled:opacity-50"
              />
            </div>
          </div>

          {response && (
            <div className="border-t border-border/40 pt-3">
              <div className="mb-2 flex flex-wrap items-center gap-4">
                <span
                  className="font-mono text-xs font-bold"
                  style={{ color: statusColor(response.status) }}
                >
                  Status: {response.status || 'ERR'}
                </span>
                <span
                  className="font-mono text-xs"
                  style={{ color: respTimeColor(response.timeMs) }}
                >
                  Time: {response.timeMs} ms
                </span>
                <span className="text-xs text-muted-foreground">
                  Size: {new Blob([response.body || '']).size} B
                </span>
              </div>
              <label className="text-xs font-semibold text-muted-foreground">
                Response Body
              </label>
              <pre className="mt-1 max-h-[360px] overflow-auto whitespace-pre-wrap break-words rounded-md border border-border/60 bg-muted/40 p-2.5 font-mono text-xs text-foreground">
                {response.body}
              </pre>
            </div>
          )}
        </div>
      )}

      <div
        className="mb-3 rounded-xl border border-border/60 bg-card px-3 py-2.5"
        style={{
          boxShadow:
            '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
        }}
      >
        <div className="flex flex-wrap items-end gap-2">
          <div className="w-[130px] space-y-0.5">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Method
            </label>
            <select
              value={filters.method}
              onChange={(e) => setFilters({ ...filters, method: e.target.value })}
              className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
            >
              <option value="">All</option>
              {methodOptions.map((m) => (
                <option key={m} value={m}>
                  {m}
                </option>
              ))}
            </select>
          </div>
          <div className="w-[140px] space-y-0.5">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Min Response (ms)
            </label>
            <input
              type="number"
              placeholder="e.g. 500"
              value={filters.minMs}
              onChange={(e) => setFilters({ ...filters, minMs: e.target.value })}
              className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
            />
          </div>
          <div className="w-[140px] space-y-0.5">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Date
            </label>
            <select
              value={filters.date}
              onChange={(e) => setFilters({ ...filters, date: e.target.value })}
              className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
            >
              {[getTodayDate(), getYesterdayDate()].map((date) => (
                <option key={date} value={date}>
                  {date}
                </option>
              ))}
            </select>
          </div>
          <div className="w-[150px] space-y-0.5">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Time Range
            </label>
            <select
              value={filters.timeRange}
              onChange={(e) =>
                setFilters({
                  ...filters,
                  timeRange: e.target.value,
                  fromTime: '',
                  toTime: '',
                })
              }
              className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
            >
              {timeOptions.map((o) => (
                <option key={o.value} value={o.value}>
                  {o.label}
                </option>
              ))}
            </select>
          </div>
          {filters.timeRange === 'custom' && (
            <>
              <div className="w-[110px] space-y-0.5">
                <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  From
                </label>
                <CalendarTimeInput
                  value={filters.fromTime}
                  onChange={(e) =>
                    setFilters({ ...filters, fromTime: e })
                  }
                  className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
                />
              </div>
              <div className="w-[110px] space-y-0.5">
                <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                  To
                </label>
                <CalendarTimeInput
                  value={filters.toTime}
                  onChange={(e) =>
                    setFilters({ ...filters, toTime: e })
                  }
                  className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
                />
              </div>
            </>
          )}
          <div className="min-w-[180px] flex-1 space-y-0.5">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Search
            </label>
            <input
              placeholder="path / url / id…"
              value={filters.search}
              onChange={(e) => setFilters({ ...filters, search: e.target.value })}
              className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
            />
          </div>
          <div className="min-w-[160px] flex-1 space-y-0.5">
            <label className="text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
              Quick Filter
            </label>
            <input
              placeholder="Filter table…"
              value={globalFilter}
              onChange={(e) => setGlobalFilter(e.target.value)}
              className="input-embossed h-9 w-full rounded-md px-2.5 text-xs"
            />
          </div>
          <Button
            variant="primary"
            size="sm"
            disabled={loading}
            onClick={() => applyFilters({ ...filters })}
          >
            Apply
          </Button>
          <Button variant="outline" size="sm" onClick={resetFilters}>
            Reset
          </Button>
        </div>
      </div>

      {/* ---- KPI tiles ---- */}
      <div className="mt-3 grid grid-cols-2 gap-3 md:grid-cols-3 lg:grid-cols-5">
        {[
          { label: 'Total Requests', value: stats.total, color: 'hsl(var(--primary))' },
          {
            label: 'Avg Response',
            value: `${stats.avgMs} ms`,
            color: respTimeColor(stats.avgMs),
          },
          {
            label: 'P95 Response',
            value: `${stats.p95Ms} ms`,
            color: respTimeColor(stats.p95Ms),
          },
          {
            label: 'Max Response',
            value: `${stats.maxMs} ms`,
            color: respTimeColor(stats.maxMs),
          },
          {
            label: 'Error Rate',
            value: `${stats.errorRate}%`,
            color: stats.errorRate > 5 ? '#ef4444' : '#22c55e',
          },
        ].map((kpi: any) => (
          <div
            key={kpi.label}
            className="flex flex-col gap-1.5 rounded-xl border border-border/60 bg-card px-4 py-3"
            style={{
              boxShadow:
                '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
            }}
          >
            <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">
              {kpi.label}
            </span>
            <span
              className="text-2xl font-bold tabular-nums leading-none"
              style={{ color: kpi.color }}
            >
              {kpi.value}
            </span>
          </div>
        ))}
      </div>

      {/* ---- Charts ---- */}
      <div
        className="mt-3"
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(320px, 1fr))',
          gap: '16px',
        }}
      >
        <div
          className="rounded-xl border border-border/60 bg-card p-3"
          style={{
            boxShadow:
              '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
          }}
        >
          <h6 className="mb-2 text-sm font-bold text-foreground">Requests Over Time</h6>
          <Chart
            type="area"
            height={260}
            options={{
              chart: { toolbar: { show: false }, zoom: { enabled: false } },
              dataLabels: { enabled: false },
              stroke: { curve: 'smooth', width: 2 },
              colors: ['#3b82f6'],
              xaxis: {
                categories: stats.timeBuckets,
                labels: { rotate: -45, style: { fontSize: '10px' } },
              },
              tooltip: { theme: 'dark' },
            } as any}
            series={[{ name: 'Requests', data: stats.countSeries }]}
          />
        </div>

        <div
          className="rounded-xl border border-border/60 bg-card p-3"
          style={{
            boxShadow:
              '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
          }}
        >
          <h6 className="mb-2 text-sm font-bold text-foreground">
            Avg Response Time Over Time (ms)
          </h6>
          <Chart
            type="line"
            height={260}
            options={{
              chart: { toolbar: { show: false }, zoom: { enabled: false } },
              dataLabels: { enabled: false },
              stroke: { curve: 'smooth', width: 2 },
              colors: ['#f59e0b'],
              xaxis: {
                categories: stats.timeBuckets,
                labels: { rotate: -45, style: { fontSize: '10px' } },
              },
              tooltip: { theme: 'dark' },
            } as any}
            series={[{ name: 'Avg ms', data: stats.avgSeries }]}
          />
        </div>

        <div
          className="rounded-xl border border-border/60 bg-card p-3"
          style={{
            boxShadow:
              '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
          }}
        >
          <h6 className="mb-2 text-sm font-bold text-foreground">
            Status Code Distribution
          </h6>
          <Chart
            type="donut"
            height={260}
            options={{
              labels: Object.keys(stats.statusDist),
              colors: ['#22c55e', '#3b82f6', '#f59e0b', '#ef4444'],
              legend: { position: 'bottom' },
              tooltip: { theme: 'dark' },
            } as any}
            series={Object.values(stats.statusDist)}
          />
        </div>

        <div
          className="rounded-xl border border-border/60 bg-card p-3"
          style={{
            boxShadow:
              '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
          }}
        >
          <h6 className="mb-2 text-sm font-bold text-foreground">Requests by Method</h6>
          <Chart
            type="bar"
            height={260}
            options={{
              chart: { toolbar: { show: false } },
              plotOptions: {
                bar: { distributed: true, columnWidth: '45%' },
              },
              dataLabels: { enabled: true },
              legend: { show: false },
              xaxis: { categories: Object.keys(stats.methodDist) },
              tooltip: { theme: 'dark' },
            } as any}
            series={[
              { name: 'Requests', data: Object.values(stats.methodDist) },
            ]}
          />
        </div>

        <div
          className="rounded-xl border border-border/60 bg-card p-3"
          style={{
            boxShadow:
              '6px 6px 14px rgba(163, 177, 198, 0.35), -6px -6px 14px rgba(255, 255, 255, 0.9)',
            gridColumn: '1 / -1',
          }}
        >
          <h6 className="mb-2 text-sm font-bold text-foreground">
            Top 10 Slowest Endpoints (avg ms)
          </h6>
          <Chart
            type="bar"
            height={340}
            options={{
              chart: { toolbar: { show: false } },
              plotOptions: {
                bar: {
                  horizontal: true,
                  distributed: true,
                  barHeight: '60%',
                },
              },
              dataLabels: {
                enabled: true,
                formatter: (v: any) => `${v} ms`,
              },
              legend: { show: false },
              colors: ['#ef4444'],
              xaxis: {
                categories: stats.slowest.map((s: any) => s.path),
              },
              tooltip: {
                theme: 'dark',
                y: {
                  formatter: (v: any, opts: any) => {
                    const row = stats.slowest[opts?.dataPointIndex];
                    return `${v} ms avg (${row?.count || 0} reqs)`;
                  },
                },
              },
            } as any}
            series={[
              { name: 'Avg ms', data: stats.slowest.map((s: any) => s.avg) },
            ]}
          />
        </div>
      </div>

      <div style={{ width: '100%', overflowX: 'auto', marginTop: 12 }}>
        <DataTable
          tableId="api-metrics"
          columns={columns}
          data={displayRows}
          isLoading={pageLoading}
          pageSize={100}
          serverPagination={{ ...paging, rowCount: total, onChange: setPaging }}
          emptyTitle="No API metrics found"
          disableExport
        />
      </div>
      </div>

      <Dialog open={!!detail} onOpenChange={(o) => !o && setDetail(null)}>
        <DialogContent className="max-h-[85vh] max-w-3xl overflow-y-auto">
          {detail && (
            <>
              <DialogHeader>
                <DialogTitle>
                  {detail.kind === 'request' ? 'Request' : 'Response'} Details
                </DialogTitle>
                <DialogDescription className="break-all font-mono text-xs">
                  {detail.row.method} {detail.row.url || detail.row.path}
                </DialogDescription>
              </DialogHeader>
              <div className="flex flex-wrap gap-x-5 gap-y-1 text-xs text-muted-foreground">
                <span>
                  Status:{' '}
                  <b style={{ color: statusColor(Number(detail.row.statusCode)) }}>
                    {detail.row.statusCode ?? '—'}
                  </b>
                </span>
                <span>
                  Time:{' '}
                  <b style={{ color: respTimeColor(detail.row.responseTimeMs) }}>
                    {detail.row.responseTimeMs ?? '—'} ms
                  </b>
                </span>
                <span>At: {fmtTime(detail.row.timestamp)}</span>
                {detail.row.userId && <span>User: {detail.row.userId}</span>}
                <span className="font-mono">ID: {detail.row.requestId || '—'}</span>
              </div>
              <div className="space-y-3">
                {detail.kind === 'request' ? (
                  <>
                    <JsonBlock label="Params" value={detail.row.params} />
                    <JsonBlock label="Query" value={detail.row.query} />
                    <JsonBlock label="Body" value={detail.row.body} />
                  </>
                ) : (
                  <JsonBlock label="Response Body" value={detail.row.response} />
                )}
              </div>
            </>
          )}
        </DialogContent>
      </Dialog>
    </FillPage>
  );
};

export default ApiMetricsPage;
